import opentype, { load, parse } from 'opentype.js';
import { CardFace, Font, InitializationData, OpenTypeFont } from '../global-types';
import { helperSettingsConfig } from './configs/helper-settings.configs';

export const getFontId = (fontFamily: string): number => {
  const id = fontFamily.split('-')[1];
  return id ? +id : 0;
};

/**
 * Get the font family name given the font id
 *
 * @param id Font id
 * @returns Font family name
 */
export const getFontName = (id: number): string => `fontid-${id}`;

export const addFontFace = async (id: number, url: string) => {
  const loadedFace = await new FontFace(getFontName(id), `url(${url})`).load();
  document.fonts.add(loadedFace);
};

export const addMultipleFontFaces = (fonts: Font[]) => {
  const addedFonts = fonts.map((font) => addFontFace(font.id, font.url));
  return Promise.all(addedFonts);
};

const parseFontsWithOpenType = (font: Font, openTypeFont: opentype.Font) => {
  const fontKey = getFontName(font.id);
  return {
    [fontKey]: {
      font: openTypeFont,
      name: font.name,
    },
  };
};

export const loadOpenTypeFonts = (fonts: Font[]): Promise<OpenTypeFont[]> => {
  const openFonts = fonts.map((font) => {
    return new Promise((resolve, rejects) => {
      load(font.url, (err, openFont) => {
        if (err) {
          rejects(err);
        } else if (openFont) {
          resolve(parseFontsWithOpenType(font, openFont));
        }
      });
    });
  });
  return Promise.all(openFonts) as Promise<OpenTypeFont[]>;
};

export const fetchAndLoadFont = async (font: Font): Promise<OpenTypeFont> => {
  const response = await fetch(font.url);
  const arrayBuffer = await response.arrayBuffer();

  const loadedFace = await new FontFace(getFontName(font.id), arrayBuffer).load();
  document.fonts.add(loadedFace);

  const openTypeFont = parse(arrayBuffer);
  return parseFontsWithOpenType(font, openTypeFont);
};

export const fetchAndLoadMultipleFonts = (fonts: Font[]): Promise<OpenTypeFont[]> => {
  return Promise.all(fonts.map((font) => fetchAndLoadFont(font)));
};

/** Default font ID used by photo text zones ("Type a Message" feature). */
export const PHOTO_TEXT_ZONE_DEFAULT_FONT_ID = 107;

export const getTemplateFontIds = (initData: InitializationData): number[] => {
  const fontIds = new Set<number>();
  const faces = initData.variables?.template_data?.Faces as CardFace[] | undefined;

  if (faces) {
    for (const face of faces) {
      if (face.Texts) {
        for (const text of face.Texts) {
          if (text.FontId) {
            fontIds.add(text.FontId);
          }
        }
      }
    }
  }

  const defaultFontId = initData.variables?.template_data?.DefaultTextArea?.FontFamilyId;
  if (defaultFontId) {
    fontIds.add(defaultFontId);
  }

  // Always include the photo text zone default font so it's eagerly loaded
  // even when the template specifies FontId: 0 (no font).
  fontIds.add(PHOTO_TEXT_ZONE_DEFAULT_FONT_ID);

  return Array.from(fontIds);
};

export const splitFontsByUsage = (
  fonts: Font[],
  templateFontIds: number[],
): { templateFonts: Font[]; remainingFonts: Font[] } => {
  const idSet = new Set(templateFontIds);
  const templateFonts: Font[] = [];
  const remainingFonts: Font[] = [];

  for (const font of fonts) {
    if (idSet.has(font.id)) {
      templateFonts.push(font);
    } else {
      remainingFonts.push(font);
    }
  }

  return { templateFonts, remainingFonts };
};

const PT_TO_PX_FACTOR = 4 / 3;

export const FONT_SCALE_FACTOR = PT_TO_PX_FACTOR * helperSettingsConfig.scalingFactor;

/**
 * Converts a given font size in points unit to the corresponding size in pixels, taking scaling factor into consideration.
 *
 * @param size Font size in Points
 * @returns Font size in Pixels
 */
export const convertPointToPixel = (size: number): number => Math.round(size * FONT_SCALE_FACTOR);

/**
 * Converts a given font size in pixels unit to the corresponding size in points, taking scaling factor into consideration.
 *
 * @param size Font size in Pixels
 * @returns Font size in Points
 */
export const convertPixelToPoint = (size: number): number => Math.round(size / FONT_SCALE_FACTOR);
